/**
 * This is the starting point of your application.
 * oneAudi OS and Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useContext, useEffect, useState } from 'react';
import { getReadyAdditionalObjects } from '../tracking/getReadyAdditionalObjects';
import { readyEvent } from '../tracking/readyEvent';
import { ContentTiles } from './components/ContentTiles';
import { ContentContext } from './context/ContentContext';
import { useTrackingService } from './hooks/useTrackingService';
import { useWltpTileFilter } from './useWltpTileFilter';
import { fetchCaeEnforcement } from './marketCompliance';
import type { GfaServiceConfigProviderV1 } from './types/feature-service-types';

interface FeatureAppProps {
  isMarketComplianceEnabled?: boolean;
  initialWltpCompliance?: boolean[];
  gfaServiceConfigProvider?: GfaServiceConfigProviderV1;
}

const FeatureApp: React.FC<FeatureAppProps> = ({
  isMarketComplianceEnabled,
  initialWltpCompliance,
  gfaServiceConfigProvider,
}) => {
  const context = useContext(ContentContext);
  const [complianceEnabled, setComplianceEnabled] = useState<boolean | undefined>(
    isMarketComplianceEnabled,
  );

  const { trackingService } = useTrackingService();

  useEffect(() => {
    if (isMarketComplianceEnabled !== undefined) return undefined;

    let cancelled = false;
    fetchCaeEnforcement({ gfaServiceConfigProvider })
      .then((result) => {
        if (!cancelled) setComplianceEnabled(result.caeDataRequired);
      })
      .catch(() => {
        if (!cancelled) setComplianceEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gfaServiceConfigProvider, isMarketComplianceEnabled]);

  const effectiveMarketCompliance = isMarketComplianceEnabled ?? complianceEnabled;
  const isComplianceDecisionPending = effectiveMarketCompliance === undefined;

  const { filteredTiles, isGateActive } = useWltpTileFilter({
    isMarketComplianceEnabled: effectiveMarketCompliance,
    initialWltpCompliance,
  });

  useEffect(() => {
    if (trackingService) {
      trackingService.track({
        event: readyEvent,
        componentUpdate: getReadyAdditionalObjects(),
      });
    }
  }, [trackingService]);

  if (isComplianceDecisionPending || (isGateActive && filteredTiles.length === 0)) {
    return null;
  }

  return <ContentTiles {...context.content} tiles={filteredTiles} />;
};

export default FeatureApp;
